import { useCallback, useEffect, useRef, useState } from 'react';

export const ReactMediaRecorder = ({
    render,
    audio = true,
    video = false,
    onStop = url => null,
    blobPropertyBag,
    screen = false,
    mediaRecorderOptions = null,
}) => {
    const mediaRecorder = useRef(null);
    const mediaChunks = useRef([]);
    const mediaStream = useRef(null);
    const [status, setStatus] = useState('idle');
    const [isAudioMuted, setIsAudioMuted] = useState(false);
    const [mediaBlobUrl, setMediaBlobUrl] = useState(null);
    const [error, setError] = useState('NONE');

    const getMediaStream = useCallback(async () => {
        setStatus('acquiring_media');
        const requiredMedia = {
            audio: typeof audio === 'boolean' ? Boolean(audio) : audio,
            video: typeof video === 'boolean' ? Boolean(video) : video,
        };
        try {
            if (screen) {
                //@ts-ignore
                const stream = await window.navigator.mediaDevices.getDisplayMedia({
                    video: video || true,
                });
                if (audio) {
                    const audioStream = await window.navigator.mediaDevices.getUserMedia({
                        audio,
                    });

                    audioStream.getAudioTracks().forEach(audioTrack => stream.addTrack(audioTrack));
                }
                mediaStream.current = stream;
            } else {
                const stream = await window.navigator.mediaDevices.getUserMedia(requiredMedia);
                mediaStream.current = stream;
            }
            setStatus('idle');
        } catch (error) {
            if (
                error.message === 'getUserMedia is not implemented in this browser' ||
                error.message.indexOf('window.navigator.mediaDevices.getDisplayMedia') >= 0
            ) {
                setError('Unsupported Browser');
            } else {
                setError(error.message || error.description || error.name);
            }
            setStatus('idle');
        }
    }, [audio, video, screen]);

    useEffect(() => {
        if (!window.MediaRecorder) {
            setError('Unsupported Browser');
            //throw new Error('Unsupported Browser');
        }

        if (screen) {
            //@ts-ignore
            if (!window.navigator?.mediaDevices?.getDisplayMedia) {
                setError("This browser doesn't support screen capturing");
                //throw new Error("This browser doesn't support screen capturing");
            }
        }

        const checkConstraints = mediaType => {
            const supportedMediaConstraints = navigator.mediaDevices.getSupportedConstraints();
            const unSupportedConstraints = Object.keys(mediaType).filter(
                constraint => !supportedMediaConstraints[constraint]
            );

            if (unSupportedConstraints.length > 0) {
                console.error(
                    `The constraints ${unSupportedConstraints.join(
                        ','
                    )} doesn't support on this browser. Please check your ReactMediaRecorder component.`
                );
            }
        };

        if (typeof audio === 'object') {
            checkConstraints(audio);
        }

        if (typeof video === 'object') {
            checkConstraints(video);
        }

        if (mediaRecorderOptions?.mimeType) {
            if (!MediaRecorder.isTypeSupported(mediaRecorderOptions.mimeType)) {
                console.error(`The specified MIME type you supplied for MediaRecorder doesn't support this browser`);
            }
        }

        async function loadStream() {
            await getMediaStream();
        }

        if (!mediaStream.current) {
            loadStream();
        }
    }, [audio, screen, video, getMediaStream, mediaRecorderOptions]);

    // Media Recorder Handlers

    const changeStream = async () => {
        setError('NONE');
        mediaStream.current.getTracks().forEach(track => track.stop());
        await getMediaStream();
    };

    const startRecording = async () => {
        setError('NONE');
        mediaChunks.current.splice(0, mediaChunks.current.length);
        if (!mediaStream.current) {
            await getMediaStream();
        }

        if (mediaStream.current) {
            mediaRecorder.current = new MediaRecorder(mediaStream.current);
            mediaRecorder.current.ondataavailable = onRecordingActive;
            mediaRecorder.current.onstop = onRecordingStop;
            mediaRecorder.current.onerror = () => {
                setError('NO_RECORDER');
                setStatus('idle');
            };
            mediaRecorder.current.start();
            setStatus('recording');
        }
    };

    const onRecordingActive = ({ data }) => {
        mediaChunks.current.push(data);
    };

    const onRecordingStop = () => {
        const blobProperty = blobPropertyBag || video ? { type: 'video/mp4' } : { type: 'audio/wav' };
        const blob = new Blob(mediaChunks.current, blobProperty);
        const url = URL.createObjectURL(blob);
        setStatus('stopped');
        setMediaBlobUrl(url);
        onStop(url);
    };

    const muteAudio = mute => {
        setIsAudioMuted(mute);
        if (mediaStream.current) {
            mediaStream.current.getAudioTracks().forEach(audioTrack => (audioTrack.enabled = !mute));
        }
    };

    const pauseRecording = () => {
        if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
            mediaRecorder.current.pause();
        }
    };

    const resumeRecording = () => {
        if (mediaRecorder.current && mediaRecorder.current.state === 'paused') {
            mediaRecorder.current.resume();
        }
    };

    const stopRecording = () => {
        if (mediaRecorder.current) {
            setStatus('stopping');
            mediaRecorder.current.stop();
        }
    };

    return render({
        error,
        muteAudio: () => muteAudio(true),
        unMuteAudio: () => muteAudio(false),
        startRecording,
        pauseRecording,
        resumeRecording,
        stopRecording,
        changeStream,
        mediaBlobUrl,
        status,
        isAudioMuted,
        previewStream: mediaStream.current?.getVideoTracks ? new MediaStream(mediaStream.current.getVideoTracks()) : null,
    });
};

export default ReactMediaRecorder;
